import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sbomApi } from './sbomApi';
import { formatStamp, formatStampShort } from '../utils/datetime';
import { health, HealthDelta, Tip } from '../pages/risk/riskUi';
import { SbomBaselineModal, SEV_COLOR, relLabel } from './SbomDashboard';
import '../styles/DocReview.css';
import '../styles/Risk.css';
import '../styles/TraceDashboard.css';
import './SbomDashboard.css';

const BUCKETS = [
  ['newly_exploited', 'Newly exploited', { background: '#7A1F05', color: '#fff' }],
  ['new', 'New findings', { background: '#FBE0D5', color: '#8A2A0B' }],
  ['lost_control', 'Lost control', { background: '#FFF1D6', color: '#7A4B00' }],
  ['fixed_by_upgrade', 'Fixed by upgrade', { background: '#DDF3E4', color: '#17603A' }],
  ['gone', 'No longer reported', { background: '#EEF6F1', color: '#2E6B4E' }],
  ['newly_controlled', 'Newly controlled', { background: '#DDF3E4', color: '#17603A' }],
  ['verdict_changed', 'Verdict changed', { background: '#E3ECF8', color: '#234B85' }],
  ['components', 'Components ±', { background: '#F3F0F8', color: '#4A4560' }],
];
const BUCKET_STYLE = Object.fromEntries(BUCKETS.map(([k, , st]) => [k, st]));
const BUCKET_LABEL = Object.fromEntries(BUCKETS.map(([k, l]) => [k, l]));

/**
 * SBOM Dashboard baselines & compare (v4.32.0, mockup board 3) — the Traceability / Risk baselines
 * screen for one release. A baseline freezes the dashboard as of the last vulnerability check; compare
 * is by vulnerability × package, one bucket per finding; the AI analysis is saved per exact change.
 */
export default function SbomBaselines({ releaseId: rid, onAuth }) {
  const [sum, setSum] = useState(null);
  const [list, setList] = useState(null);
  const [a, setA] = useState('');
  const [b, setB] = useState('now');
  const [cmp, setCmp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bucket, setBucket] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [taking, setTaking] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, bl] = await Promise.all([sbomApi.dashboard(rid), sbomApi.dashBaselines(rid)]);
      setSum(d);
      setList(bl.baselines || []);
      const live = (bl.baselines || []).filter((x) => !x.archived_at);
      setA((cur) => cur || (live[0]?.id || ''));
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
      setList((x) => x || []);
    }
  }, [rid, onAuth]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!a) { setCmp(null); return undefined; }
    let live = true;
    setLoading(true);
    setBucket('');
    sbomApi.dashCompare(rid, a, b)
      .then((d) => { if (live) { setCmp(d); setAnalysis(d.analysis || null); setError(''); } })
      .catch((e) => { if (live && !onAuth(e)) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [rid, a, b, onAuth]);

  const analyse = async (force = false) => {
    setAnalysing(true);
    try {
      const r = await sbomApi.dashAnalyse(rid, { a, b, force });
      setAnalysis(r.analysis);
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
    } finally {
      setAnalysing(false);
    }
  };

  const archive = async (x) => {
    try { await sbomApi.archiveDashBaseline(rid, x.id, !x.archived_at); load(); } catch (e) { if (!onAuth(e)) setError(e.message); }
  };

  const rel = sum?.summary?.release;
  const relName = relLabel(rel) || 'Release';
  const aiEnabled = !!sum?.ai;
  const live = (list || []).filter((x) => !x.archived_at);
  const changes = (cmp?.diff?.changes || []).filter((c) => !bucket || c.bucket === bucket);
  const sa = health(cmp?.score_a?.score);
  const sb = health(cmp?.score_b?.score);

  return (
    <div className="drv-page rsk-page sbd-page">
      <div className="rsk-crumbs">
        <Link to="/bom/sw-dashboard">Software BOM · Dashboard</Link><span>/</span>
        <Link to={`/bom/sw-dashboard/${rid}`}>{relName}</Link><span>/</span>
        <strong>Baselines</strong>
      </div>
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Baselines — {relName}</h1>
          <p className="drv-sub">Read-only snapshots of this release&apos;s vulnerabilities — compare any two, or a baseline with now.</p>
        </div>
        <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => setTaking(true)} disabled={!sum}>Take baseline</button>
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}

      <div className="rsk-two">
        <aside className="rsk-bl-list" aria-label="Baselines">
          <div className="rsk-lbl">Baselines · {live.length}</div>
          {list === null && <div className="drv-muted">Loading…</div>}
          {list?.length === 0 && (
            <div className="drv-empty" style={{ padding: 18 }}>
              No baselines yet. Take one before a cybersecurity review or a submission — then every later change can be
              compared against it.
            </div>
          )}
          {(list || []).map((x) => (
            <div key={x.id} className={`rsk-bl${x.archived_at ? ' rsk-bl--arch' : ''}`}>
              <div className="rsk-row-between">
                <strong>{x.name}</strong>
                <span className="rsk-bl-score">{health(x.score) ?? '—'}</span>
              </div>
              <div className="rsk-fine">{formatStamp(x.created_at, { dateOnly: true })} · {x.created_by_name} · {x.n_findings} findings</div>
              <Tip as="div" className="rsk-sha" heading="SHA-256 fingerprint"
                tip={<span className="rsk-tip-mono">{x.sha256}</span>}>SHA-256 {x.sha256?.slice(0, 4)}…{x.sha256?.slice(-4)}</Tip>
              {x.note && <div className="rsk-fine">{x.note}</div>}
              <button type="button" className="drv-link rsk-small-link" style={{ marginLeft: 0, alignSelf: 'flex-start' }}
                onClick={() => archive(x)}>{x.archived_at ? 'Restore' : 'Archive'}</button>
            </div>
          ))}
          <div className="rsk-fine">
            A baseline keeps every finding, its VEX verdict, its risk control, KEV status and the score, as of the
            vulnerability check it was taken after. It can be archived but never edited or deleted.
          </div>
        </aside>

        <section className="rsk-cmp" aria-label="Compare">
          {live.length > 0 && (
            <div className="rsk-card rsk-pad rsk-cmp-bar">
              <label className="drv-field">
                <span className="drv-lbl">From</span>
                <select className="drv-select" value={a} onChange={(e) => setA(e.target.value)}>
                  {live.map((x) => <option key={x.id} value={x.id}>{x.name} — {formatStamp(x.created_at, { dateOnly: true })} · score {health(x.score) ?? '—'}</option>)}
                </select>
              </label>
              <span className="rsk-arr" aria-hidden="true">→</span>
              <label className="drv-field">
                <span className="drv-lbl">To</span>
                <select className="drv-select" value={b} onChange={(e) => setB(e.target.value)}>
                  <option value="now">Now{sum?.read_at ? ` (checked ${formatStampShort(sum.read_at)})` : ''}</option>
                  {live.filter((x) => x.id !== a).map((x) => <option key={x.id} value={x.id}>{x.name} — {formatStamp(x.created_at, { dateOnly: true })}</option>)}
                </select>
              </label>
            </div>
          )}

          {loading && <div className="drv-muted">Comparing…</div>}

          {cmp && !loading && (
            <>
              <div className="rsk-tiles">
                <div className="rsk-tile rsk-tile--static">
                  <span className="rsk-tile-name">Exposure score</span>
                  <span className="rsk-tile-n">{sa ?? '—'} → {sb ?? '—'}
                    {sa != null && sb != null && sa !== sb && (
                      <HealthDelta from={sa} to={sb} />
                    )}
                  </span>
                  <span className="rsk-tile-hint">{cmp.diff.n_a} → {cmp.diff.n_b} findings</span>
                </div>
                <button type="button" className={`rsk-tile${!bucket ? ' rsk-tile--on' : ''}`} onClick={() => setBucket('')}>
                  <span className="rsk-tile-name">All changes</span>
                  <span className="rsk-tile-n">{cmp.diff.changes.length}</span>
                  <span className="rsk-tile-hint">{cmp.diff.unchanged} unchanged</span>
                </button>
                {BUCKETS.map(([k, label]) => (
                  <button key={k} type="button" className={`rsk-tile${bucket === k ? ' rsk-tile--on' : ''}`}
                    onClick={() => setBucket(bucket === k ? '' : k)} disabled={!cmp.diff.counts[k]}>
                    <span className="rsk-tile-name">{label}</span>
                    <span className="rsk-tile-n">{cmp.diff.counts[k]}</span>
                    <span className="rsk-tile-hint">{cmp.diff.hints?.[k]}</span>
                  </button>
                ))}
              </div>

              <div className="rsk-card rsk-pad rsk-ai">
                <div className="rsk-row-between">
                  <div className="rsk-lbl">AI analysis of this change</div>
                  {analysis && (
                    <span className="rsk-fine">
                      {analysis.created_at ? `${formatStamp(analysis.created_at)} · ` : ''}
                      {aiEnabled && <button type="button" className="drv-link rsk-small-link" onClick={() => analyse(true)} disabled={analysing}>Regenerate</button>}
                    </span>
                  )}
                </div>
                {!analysis && (
                  aiEnabled ? (
                    <button type="button" className="drv-btn" style={{ alignSelf: 'flex-start' }} onClick={() => analyse(false)} disabled={analysing}>
                      {analysing ? 'Analysing…' : 'Analyse with AI'}
                    </button>
                  ) : <div className="rsk-fine">AI is not enabled for this account.</div>
                )}
                {analysis && (
                  <>
                    <p className="rsk-ai-text">{analysis.summary}</p>
                    {analysis.worth_a_look?.length > 0 && <div className="rsk-lbl">Worth a look</div>}
                    {(analysis.worth_a_look || []).map((w) => (
                      <div className="rsk-look" key={w.text}>
                        <span className="rsk-pill" style={{ background: '#FFF1D6', color: '#7A4B00' }}>{w.kind}</span>
                        <span className="rsk-look-text">{w.text}</span>
                        <span className="rsk-look-keys">{(w.keys || []).map((k) => <span key={k}>{k}</span>)}</span>
                      </div>
                    ))}
                    <div className="rsk-fine">AI suggestions — check them against the findings before acting.</div>
                  </>
                )}
              </div>

              <div className="rsk-card">
                <div className="rsk-pad rsk-sub" style={{ borderBottom: '1px solid #EFECF6' }}>
                  <strong style={{ color: '#1A1631' }}>{bucket ? `${changes.length} ${BUCKET_LABEL[bucket].toLowerCase()}` : `${cmp.diff.changes.length} changed · ${cmp.diff.unchanged} unchanged`}</strong>
                  {' '}· matched by vulnerability × package
                </div>
                <div className="rsk-table-wrap">
                  <table className="rsk-table" style={{ minWidth: 960 }}>
                    <thead><tr><th>Change</th><th>Component · version</th><th>Finding</th><th>Before</th><th>After</th></tr></thead>
                    <tbody>
                      {changes.map((c, i) => (
                        <tr key={`${c.bucket}-${c.key}-${i}`}>
                          <td><span className="rsk-pill" style={BUCKET_STYLE[c.bucket]}>{BUCKET_LABEL[c.bucket]}</span></td>
                          <td><strong>{c.component}</strong></td>
                          <td>
                            {c.vuln_id ? <span className="rsk-key">{c.vuln_id}</span> : <span className="rsk-dim">—</span>}
                            {c.severity && <div className="sbd-cmp-sev"><i className="sbd-dot" style={{ background: SEV_COLOR[c.severity] }} />{c.severity}</div>}
                          </td>
                          <td className="rsk-sub">{c.before}</td>
                          <td className="rsk-sub">{c.after}{(c.details || []).map((d) => <div key={d} className="rsk-fine">{d}</div>)}</td>
                        </tr>
                      ))}
                      {changes.length === 0 && <tr><td colSpan={5} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No changes.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {taking && (
        <SbomBaselineModal rid={rid} readAt={sum?.read_at} onClose={() => setTaking(false)}
          onTaken={(bl) => { setTaking(false); setA(bl.id); load(); }} />
      )}
    </div>
  );
}
